import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
  LinearProgress,
  Stack,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import AddTaskIcon from '@mui/icons-material/AddTask';
import EditNoteIcon from '@mui/icons-material/EditNote';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useBrand } from '../contexts/BrandContext';
import { useAuth } from '../contexts/AuthContext';
import { auditsApi } from '../services/audits';
import { formatDate } from '../i18n/format';
import { proposedArticlesApi } from '../services/proposed-articles';
import { tasksApi } from '../services/tasks';
import { api } from '../services/api';
import type { Audit, WebsiteAuditSummary, WebsiteAgentType, AgentStatus, TriageLog, TriageDecision, PageType, TriageReason, BusinessReadability } from '@traaker/shared';
import { classifyRecommendation, fromStringList, websiteAgentsForTier, resolveEntitlement, isSelfServeTier } from '@traaker/shared';
import { useToast } from '../contexts/ToastContext';
import { RecommendationList } from '../components/recommendations/RecommendationList';
import QuotaUpgradeDialog from '../components/billing/QuotaUpgradeDialog';
import { useTranslation, Trans } from 'react-i18next';

const STATUS_COLORS: Record<string, 'default' | 'info' | 'success' | 'error'> = {
  pending: 'default',
  running: 'info',
  completed: 'success',
  failed: 'error',
};

/** i18n keys — resolved with `t()` at render. */
const STATUS_LABEL_KEYS: Record<string, string> = {
  pending: 'auditDetail.status.pending',
  running: 'auditDetail.status.running',
  completed: 'auditDetail.status.completed',
  failed: 'auditDetail.status.failed',
};

/** i18n keys — resolved with `t()` at render. */
const AGENT_LABEL_KEYS: Record<WebsiteAgentType, string> = {
  crawl: 'auditDetail.agent.crawl',
  content_freshness: 'auditDetail.agent.contentFreshness',
  structural_seo: 'auditDetail.agent.structuralSeo',
  content_quality: 'auditDetail.agent.contentQuality',
};

const AGENT_STATUS_COLORS: Record<AgentStatus, string> = {
  pending: '#A0A4AE',
  running: '#F59E0B',
  completed: '#10B981',
  failed: '#EF4444',
};

const AGENT_KEYS: WebsiteAgentType[] = ['crawl', 'content_freshness', 'structural_seo', 'content_quality'];
const TAB_AGENTS = ['content_freshness', 'structural_seo', 'content_quality'] as const;

/**
 * Compact per-row action button for a Website-audit finding. The target
 * (task / content / GEO-strategy) and task category are DERIVED from the
 * finding text + dimension by the shared deterministic classifier — so a
 * freshness reco like "Ajouter datePublished en JSON-LD" becomes a `schema`
 * TASK, not a `content` one (the historical bug). A `content` finding routes to
 * the Studio de création instead of Tasks. Fires a global toast on success.
 */
function AddTaskButton({
  title,
  description,
  pageUrl,
  dimension,
  auditId,
  brandId,
  originLabel,
}: {
  title: string;
  description: string;
  pageUrl?: string;
  dimension?: string;
  auditId?: string;
  brandId?: string;
  /** Human label for the finding's origin, e.g. "Fraîcheur", "Structure SEO". */
  originLabel: string;
}) {
  const { t } = useTranslation();
  const { tenant } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [quotaDialogOpen, setQuotaDialogOpen] = useState(false);

  const tasksEnabled = !!tenant?.settings?.featureFlags?.tasksEnabled;
  const newsWatchEnabled = !!tenant?.settings?.featureFlags?.newsWatchEnabled;

  const { target, category } = classifyRecommendation({
    text: `${title} ${description}`,
    dimension,
    auditType: 'website',
  });
  // A content finding routes to the Studio; if News Watch is off, fall back to a
  // task. GEO-strategy findings from a website audit are rare — treat as a task.
  const effectiveTarget = target === 'content' && !newsWatchEnabled ? 'task' : target;
  const asContent = effectiveTarget === 'content';

  if (asContent ? !newsWatchEnabled : !tasksEnabled) return null;

  const handleAdd = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (done || busy) return;
    setBusy(true);
    try {
      if (asContent && brandId) {
        // Save the finding as a reusable content topic (pickable in the Studio),
        // do NOT generate.
        await proposedArticlesApi.createContentTopic({
          brandId,
          topic: `${title} — ${description}`.trim(),
          originLabel: t('auditDetail.addTask.contentOriginLabel', { origin: originLabel }),
          sourceAuditId: auditId,
        });
        toast.show(t('auditDetail.addTask.addedToStudio'), {
          action: { label: t('auditDetail.addTask.view'), onClick: () => navigate('/news-watch', { state: { focusReco: true } }) },
        });
      } else {
        await tasksApi.create({
          title: title.slice(0, 120),
          description,
          category,
          sourceType: 'audit_website',
          auditId,
          brandId,
          sourcePageUrl: pageUrl,
          sourceDimension: dimension,
          sourceAuditType: 'website',
          originLabel,
        });
        toast.show(t('auditDetail.addTask.addedToTasks'), { action: { label: t('auditDetail.addTask.view'), onClick: () => navigate('/tasks') } });
      }
      setDone(true);
    } catch (err: any) {
      if (err?.body?.code === 'QUOTA_EXCEEDED') {
        setQuotaDialogOpen(true);
      } else {
        toast.show(t('auditDetail.addTask.error'), { severity: 'error' });
      }
    } finally {
      setBusy(false);
    }
  };

  const tip = done
    ? asContent ? t('auditDetail.addTask.addedToStudioShort') : t('auditDetail.addTask.addedToTasks')
    : asContent ? t('auditDetail.addTask.addToStudio') : t('auditDetail.addTask.addToTasks');

  return (
    <>
      <Tooltip title={tip}>
        <IconButton size="small" onClick={handleAdd} disabled={busy} sx={{ ml: 0.5 }}>
          {busy ? (
            <CircularProgress size={14} />
          ) : done ? (
            <CheckCircleIcon fontSize="small" sx={{ color: '#00DDB3' }} />
          ) : asContent ? (
            <EditNoteIcon fontSize="small" sx={{ color: '#0284C7' }} />
          ) : (
            <AddTaskIcon fontSize="small" sx={{ color: '#7C3AED' }} />
          )}
        </IconButton>
      </Tooltip>
      <QuotaUpgradeDialog
        open={quotaDialogOpen}
        onClose={() => setQuotaDialogOpen(false)}
        highlightMetric="tasks"
      />
    </>
  );
}

function ScoreBadge({ score }: { score: number }) {
  const color = score >= 70 ? '#00DDB3' : score >= 40 ? '#FF6B35' : '#FF4444';
  return (
    <Typography fontWeight={700} sx={{ color }}>
      {score}/100
    </Typography>
  );
}

export default function AuditDetail() {
  const { t } = useTranslation();
  const { auditId } = useParams<{ auditId: string }>();
  const navigate = useNavigate();
  const { activeBrand } = useBrand();
  const { tenant } = useAuth();
  const [audit, setAudit] = useState<Audit | null>(null);
  const [summary, setSummary] = useState<WebsiteAuditSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [tabResults, setTabResults] = useState<any[]>([]);
  const [loadingResults, setLoadingResults] = useState(false);
  const [triage, setTriage] = useState<TriageLog | null>(null);
  const [pagesQuotaOpen, setPagesQuotaOpen] = useState(false);
  const [tierUpgradeOpen, setTierUpgradeOpen] = useState(false);

  const loadAudit = useCallback(async () => {
    if (!auditId) return;
    try {
      const [auditData, summaryData, triageData] = await Promise.all([
        auditsApi.getById(auditId),
        auditsApi.getSummary(auditId).catch(() => null),
        auditsApi.getTriage(auditId).catch(() => null),
      ]);
      setAudit(auditData);
      setSummary(summaryData);
      setTriage(triageData);
    } catch {
      // handled below
    } finally {
      setLoading(false);
    }
  }, [auditId]);

  useEffect(() => {
    loadAudit();
  }, [loadAudit]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadAudit();
    // Also reload current tab results
    if (auditId) {
      try {
        const results = await auditsApi.getResults(auditId, TAB_AGENTS[activeTab]);
        setTabResults(results);
      } catch { /* ignore */ }
    }
    setRefreshing(false);
  };

  const handleTabChange = async (_: any, newTab: number) => {
    setActiveTab(newTab);
    if (!auditId) return;
    setLoadingResults(true);
    try {
      const results = await auditsApi.getResults(auditId, TAB_AGENTS[newTab]);
      setTabResults(results);
    } catch {
      setTabResults([]);
    } finally {
      setLoadingResults(false);
    }
  };

  const goToContentQualityTab = () => handleTabChange(null, 2);

  // Load first tab on mount when audit is ready
  useEffect(() => {
    if (!auditId || loading) return;
    if (audit?.status === 'completed' || audit?.agentStatuses?.content_freshness === 'completed') {
      setLoadingResults(true);
      auditsApi.getResults(auditId, TAB_AGENTS[0])
        .then(setTabResults)
        .catch(() => setTabResults([]))
        .finally(() => setLoadingResults(false));
    }
  }, [auditId, loading, audit?.status, audit?.agentStatuses?.content_freshness]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!audit) {
    return <Typography>{t('auditDetail.notFound')}</Typography>;
  }

  return (
    <Box>
      {/* Header */}
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/audits')} sx={{ mb: 2 }}>
        {t('auditDetail.backToAudits')}
      </Button>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Typography variant="h4" fontWeight={700}>
          {t('auditDetail.title', { date: formatDate(audit.createdAt) })}
        </Typography>
        <Chip
          label={STATUS_LABEL_KEYS[audit.status] ? t(STATUS_LABEL_KEYS[audit.status]) : audit.status}
          color={STATUS_COLORS[audit.status] || 'default'}
        />
        <Tooltip title={t('auditDetail.refresh')}>
          <IconButton onClick={handleRefresh} disabled={refreshing}>
            {refreshing ? <CircularProgress size={20} /> : <RefreshIcon />}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Agent status badges — only agents the tier actually runs */}
      {audit.agentStatuses && (
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          {AGENT_KEYS.filter((agent) => websiteAgentsForTier(tenant?.tier).includes(agent)).map((agent) => {
            const status = audit.agentStatuses?.[agent] || 'pending';
            return (
              <Chip
                key={agent}
                label={`${t(AGENT_LABEL_KEYS[agent])}: ${STATUS_LABEL_KEYS[status] ? t(STATUS_LABEL_KEYS[status]) : status}`}
                size="small"
                sx={{
                  bgcolor: `${AGENT_STATUS_COLORS[status]}20`,
                  color: AGENT_STATUS_COLORS[status],
                  fontWeight: 600,
                }}
              />
            );
          })}
        </Box>
      )}

      {audit.status === 'running' && <LinearProgress sx={{ mb: 3, borderRadius: 1 }} />}

      {audit.status === 'pending' && (
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography color="text.secondary">
              {t('auditDetail.pendingProcessing')}
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Summary cards */}
      {summary && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
            <SummaryCard title={t('auditDetail.summary.websiteScore')} value={`${summary.overallGeoScore}/100`} color="#7C3AED" />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
            <SummaryCard
              title={t('auditDetail.summary.freshness')}
              value={`${summary.content_freshness?.avg_freshness_score ?? '—'}`}
              color="#00DDB3"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
            <SummaryCard
              title={t('auditDetail.summary.structuralSeo')}
              value={`${summary.structural_seo?.avg_structural_score ?? '—'}`}
              color="#8B5CF6"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
            <SummaryCard
              title={t('auditDetail.summary.contentQuality')}
              value={`${summary.content_quality?.avg_geo_readiness ?? '—'}`}
              color="#FF6B35"
            />
          </Grid>
          {summary.content_quality?.avg_brand_alignment != null && (
            <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
              <SummaryCard
                title={t('auditDetail.summary.brandAlignment')}
                value={`${summary.content_quality.avg_brand_alignment}`}
                color="#E91E63"
              />
            </Grid>
          )}
          <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
            <PagesAnalyzedCard pagesAnalyzed={summary.pagesAnalyzed} onUpgrade={() => setPagesQuotaOpen(true)} />
          </Grid>
        </Grid>
      )}

      <QuotaUpgradeDialog
        open={pagesQuotaOpen}
        onClose={() => setPagesQuotaOpen(false)}
        highlightMetric="pages_per_website_audit"
        body={t('auditDetail.pagesAnalyzed.capExplainer', {
          tier: tenant?.tier ? tenant.tier.charAt(0).toUpperCase() + tenant.tier.slice(1) : 'Basic',
          limit: resolveEntitlement('pages_per_website_audit', tenant?.tier || 'basic').limit,
        })}
      />
      <QuotaUpgradeDialog
        open={tierUpgradeOpen}
        onClose={() => setTierUpgradeOpen(false)}
        openPlansDirectly
      />

      {/* Phase A: triage disclosure — only rendered when triage.json exists (post-Phase-A audits) */}
      {triage && <TriagePanel triage={triage} brandId={audit.brandId} />}

      {/* Tabs for agent results */}
      <Card sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange} sx={{ px: 2, pt: 1 }}>
          <Tab label={t('auditDetail.summary.freshness')} />
          <Tab label={t('auditDetail.summary.structuralSeo')} />
          <Tab label={t('auditDetail.summary.contentQuality')} />
        </Tabs>

        {loadingResults ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : tabResults.length === 0 &&
          audit.status === 'completed' &&
          activeTab !== 2 &&
          audit.agentStatuses?.[TAB_AGENTS[activeTab]] === 'pending' &&
          // Self-serve only: agency/enterprise DO run freshness + structural SEO
          // (WEBSITE_AGENTS_BY_TIER), so a `pending` agent there means the run
          // stalled — not a tier lock. Showing them "Passez à Pro" was wrong;
          // they fall through to the generic no-results message instead.
          isSelfServeTier(tenant?.tier) ? (
          <CardContent sx={{ textAlign: 'center', py: 5, px: 3 }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                mx: 'auto',
                mb: 2,
                borderRadius: '50%',
                bgcolor: '#FEF3C7',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <LockOutlinedIcon sx={{ color: '#D97706', fontSize: 28 }} />
            </Box>
            <Typography variant="h6" fontWeight={800} sx={{ mb: 1 }}>
              {t('auditDetail.tierLocked.title')}
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3, maxWidth: 480, mx: 'auto' }}>
              {t('auditDetail.tierLocked.message')}
            </Typography>
            <Stack direction="row" spacing={1.5} justifyContent="center">
              <Button
                variant="outlined"
                onClick={goToContentQualityTab}
                sx={{ textTransform: 'none', fontWeight: 700, borderColor: '#0284C7', color: '#0284C7' }}
              >
                {t('auditDetail.tierLocked.seeResults')}
              </Button>
              <Button
                variant="contained"
                onClick={() => setTierUpgradeOpen(true)}
                sx={{ textTransform: 'none', fontWeight: 700, bgcolor: '#7C3AED', '&:hover': { bgcolor: '#6D28D9' } }}
              >
                {t('auditDetail.tierLocked.upgrade')}
              </Button>
            </Stack>
          </CardContent>
        ) : tabResults.length === 0 ? (
          <CardContent>
            <Typography color="text.secondary" textAlign="center">
              {audit.status === 'completed' ? t('auditDetail.noResults') : t('auditDetail.resultsPending')}
            </Typography>
          </CardContent>
        ) : (
          <>
            {/* Freshness tab */}
            {activeTab === 0 && (
              <>
              <Box sx={{ px: 3, pt: 2, pb: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                  <Trans
                    i18nKey="auditDetail.freshness.intro"
                    components={{ strong: <strong />, code: <code /> }}
                  />
                </Typography>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell width={40} />
                      <TableCell>{t('auditDetail.table.page')}</TableCell>
                      <TableCell>{t('auditDetail.table.publishedDate')}</TableCell>
                      <TableCell>{t('auditDetail.table.lastModified')}</TableCell>
                      <TableCell>{t('auditDetail.table.ageDays')}</TableCell>
                      <TableCell>{t('auditDetail.table.score')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tabResults.map((r: any) => (
                      <FreshnessRow key={r.urlHash || r.SK} row={r} auditId={auditId} brandId={activeBrand?.brandId} />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              </>
            )}

            {/* Structural SEO tab */}
            {activeTab === 1 && (
              <>
                <CrawlabilityPanel
                  siteOverview={tabResults.find((r: any) => r.scope === 'site_overview' || r.SK === 'STRUCTURAL_SEO#SITE_OVERVIEW')}
                />
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell width={40} />
                        <TableCell>{t('auditDetail.table.page')}</TableCell>
                        <TableCell>{t('auditDetail.table.headings')}</TableCell>
                        <TableCell>{t('auditDetail.table.contentStructure')}</TableCell>
                        <TableCell>{t('auditDetail.table.schema')}</TableCell>
                        <TableCell>{t('auditDetail.table.titleCol')}</TableCell>
                        <TableCell>{t('auditDetail.table.meta')}</TableCell>
                        <TableCell>{t('auditDetail.table.score')}</TableCell>
                        <TableCell>{t('auditDetail.table.issues')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tabResults
                        .filter((r: any) => r.scope !== 'site_overview' && r.SK !== 'STRUCTURAL_SEO#SITE_OVERVIEW')
                        .map((r: any) => (
                          <StructuralSeoRow key={r.urlHash || r.SK} row={r} auditId={auditId} brandId={activeBrand?.brandId} />
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}

            {/* Content Quality tab — expandable rows with lazy-loaded detail */}
            {activeTab === 2 && (
              <>
                {summary?.content_quality?.business_readability &&
                  summary.content_quality.business_readability.synthesis && (
                    <BusinessReadabilityCard
                      data={summary.content_quality.business_readability}
                      auditId={auditId}
                      brandId={activeBrand?.brandId}
                    />
                  )}
                {summary?.content_quality?.page_type_averages && (
                  <PageTypeRollup averages={summary.content_quality.page_type_averages} />
                )}
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell width={40} />
                        <TableCell>{t('auditDetail.table.page')}</TableCell>
                        <TableCell>{t('auditDetail.table.type')}</TableCell>
                        <TableCell>{t('auditDetail.table.stats')}</TableCell>
                        <TableCell>{t('auditDetail.table.citations')}</TableCell>
                        <TableCell>{t('auditDetail.table.sources')}</TableCell>
                        <TableCell>{t('auditDetail.table.fluency')}</TableCell>
                        <TableCell>{t('auditDetail.summary.websiteScore')}</TableCell>
                        {tabResults.some((r: any) => r.brandAlignment) && (
                          <TableCell>{t('auditDetail.summary.brandAlignment')}</TableCell>
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tabResults.map((r: any) => (
                        <ContentQualityRow
                          key={r.urlHash || r.SK}
                          row={r}
                          auditId={auditId!}
                          brandId={activeBrand?.brandId}
                          showBrandAlignment={tabResults.some((r: any) => r.brandAlignment)}
                        />
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </>
        )}
      </Card>
    </Box>
  );
}

// --- Dimension-based advice for Structural SEO ---

/** i18n keys — resolved with `t()` at render. */
const STRUCTURAL_DIMENSION_LABEL_KEYS: Record<string, string> = {
  headings: 'auditDetail.dimension.headings',
  in_content_structure: 'auditDetail.dimension.inContentStructure',
  schema: 'auditDetail.dimension.schema',
  title: 'auditDetail.dimension.title',
  meta_description: 'auditDetail.dimension.metaDescription',
  machine_readability: 'auditDetail.dimension.machineReadability',
  micro_structure: 'auditDetail.dimension.microStructure',
  internal_linking: 'auditDetail.dimension.internalLinking',
};

const STRUCTURAL_DIMENSION_WEIGHTS: Record<string, string> = {
  headings: '25%',
  in_content_structure: '25%',
  schema: '20%',
  title: '10%',
  meta_description: '5%',
  machine_readability: '5%',
  micro_structure: '5%',
  internal_linking: '5%',
};

/** Returns the i18n key for the advice, or null when the score is fine. */
function getStructuralAdviceKey(dimension: string, score: number): string | null {
  if (score >= 70) return null;
  const keys: Record<string, string> = {
    headings: 'auditDetail.structuralAdvice.headings',
    in_content_structure: 'auditDetail.structuralAdvice.inContentStructure',
    schema: 'auditDetail.structuralAdvice.schema',
    title: 'auditDetail.structuralAdvice.title',
    meta_description: 'auditDetail.structuralAdvice.metaDescription',
    machine_readability: 'auditDetail.structuralAdvice.machineReadability',
    micro_structure: 'auditDetail.structuralAdvice.microStructure',
    internal_linking: 'auditDetail.structuralAdvice.internalLinking',
  };
  return keys[dimension] ?? null;
}

const SEVERITY_COLORS: Record<string, 'error' | 'warning' | 'info' | 'default'> = {
  blocker: 'error',
  critical: 'error',
  warning: 'warning',
  info: 'info',
};

const AI_BOT_LABELS: Record<string, string> = {
  GPTBot: 'OpenAI GPTBot',
  'ChatGPT-User': 'ChatGPT-User',
  'Google-Extended': 'Google-Extended (Gemini)',
  'anthropic-ai': 'Anthropic (legacy)',
  ClaudeBot: 'ClaudeBot',
  PerplexityBot: 'PerplexityBot',
  Bytespider: 'ByteSpider (TikTok)',
};

function CrawlabilityPanel({ siteOverview }: { siteOverview: any }) {
  const { t } = useTranslation();
  if (!siteOverview) return null;
  const robots = siteOverview.robotsAnalysis ?? {};
  const llms = siteOverview.llmsTxtAnalysis ?? {};
  if (!robots.checked && !llms.checked) return null;

  const blocked: string[] = robots.ai_bots_blocked ?? [];
  const allowed: string[] = robots.ai_bots_allowed ?? [];
  const hasRobots = robots.has_robots_txt === true;
  const robotsSeverity = blocked.length === 0 ? 'ok' : blocked.length >= 7 ? 'critical' : 'warning';
  const robotsColor = robotsSeverity === 'ok' ? '#00DDB3' : robotsSeverity === 'warning' ? '#FF6B35' : '#FF4444';

  const llmsPresent = llms.present === true;

  return (
    <Box sx={{ px: 3, pt: 2, pb: 1 }}>
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
        {t('auditDetail.crawlability.title')}
      </Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 8 }}>
          <Box sx={{ p: 2, bgcolor: `${robotsColor}15`, borderRadius: 1, border: `1px solid ${robotsColor}` }}>
            <Typography variant="body2" fontWeight={700} sx={{ color: robotsColor, mb: 0.5 }}>
              {hasRobots
                ? t('auditDetail.crawlability.robotsAllowed', { allowed: allowed.length, total: allowed.length + blocked.length })
                : t('auditDetail.crawlability.robotsNotFound')}
            </Typography>
            {hasRobots ? (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                {allowed.map((bot) => (
                  <Chip key={bot} label={AI_BOT_LABELS[bot] ?? bot} size="small" color="success" variant="outlined" />
                ))}
                {blocked.map((bot) => (
                  <Chip key={bot} label={AI_BOT_LABELS[bot] ?? bot} size="small" color="error" variant="outlined" />
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t('auditDetail.crawlability.robotsHint')}
              </Typography>
            )}
          </Box>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Box sx={{ p: 2, bgcolor: llmsPresent ? 'rgba(16,185,129,0.10)' : 'rgba(245,158,11,0.10)', borderRadius: 1, border: `1px solid ${llmsPresent ? '#10B981' : '#F59E0B'}` }}>
            <Typography variant="body2" fontWeight={700} sx={{ color: llmsPresent ? '#10B981' : '#F59E0B', mb: 0.5 }}>
              {llmsPresent ? t('auditDetail.crawlability.llmsPresent') : t('auditDetail.crawlability.llmsAbsent')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {llmsPresent
                ? t('auditDetail.crawlability.llmsPresentHint')
                : t('auditDetail.crawlability.llmsAbsentHint')}
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}

function StructuralSeoRow({ row, auditId, brandId }: { row: any; auditId?: string; brandId?: string }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const scores = row.dimensionScores ?? {};
  const issues: any[] = row.issues ?? [];

  // Dimensions ordered by weight
  const dimensionOrder = ['headings', 'in_content_structure', 'schema', 'title', 'meta_description', 'internal_linking', 'machine_readability', 'micro_structure'];
  const lowDimensions = dimensionOrder.filter(d => (scores[d] ?? 0) < 70);

  return (
    <>
      <TableRow sx={{ '& > *': { borderBottom: 'unset' }, cursor: 'pointer' }} onClick={() => setOpen(!open)}>
        <TableCell>
          <IconButton size="small">
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ maxWidth: 280 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
            <Typography variant="body2" noWrap title={row.pageTitle || row.pageUrl} sx={{ flex: 1, minWidth: 0 }}>
              {row.pageTitle || row.pageUrl}
            </Typography>
            <Tooltip title={t('auditDetail.openPage')}>
              <IconButton
                size="small"
                component="a"
                href={row.pageUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
              >
                <OpenInNewIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          </Box>
        </TableCell>
        <TableCell><ScoreBadge score={scores.headings ?? 0} /></TableCell>
        <TableCell><ScoreBadge score={scores.in_content_structure ?? 0} /></TableCell>
        <TableCell><ScoreBadge score={scores.schema ?? 0} /></TableCell>
        <TableCell><ScoreBadge score={scores.title ?? 0} /></TableCell>
        <TableCell><ScoreBadge score={scores.meta_description ?? 0} /></TableCell>
        <TableCell><ScoreBadge score={row.structuralScore ?? 0} /></TableCell>
        <TableCell>
          {issues.length > 0 && (
            <Chip
              label={`${issues.length}`}
              size="small"
              color={issues.some((i: any) => i.severity === 'critical' || i.severity === 'blocker') ? 'error' : 'warning'}
            />
          )}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell sx={{ py: 0 }} colSpan={9}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ py: 2, px: 1 }}>
              <Grid container spacing={3}>
                <Grid size={12}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', wordBreak: 'break-all' }}>
                    {row.pageUrl}
                  </Typography>
                </Grid>
                {/* Rendering prerequisite */}
                {row.renderingPass === false && (
                  <Grid size={{ xs: 12 }}>
                    <Box sx={{ p: 2, bgcolor: '#FF444415', borderRadius: 1, border: '1px solid #FF4444' }}>
                      <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#FF4444', mb: 0.5 }}>
                        {t('auditDetail.csr.title')}
                      </Typography>
                      <Typography variant="body2">
                        {t('auditDetail.csr.body')}
                      </Typography>
                    </Box>
                  </Grid>
                )}

                {/* Dimension scores with advice */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                    {t('auditDetail.structural.dimensionScores')}
                  </Typography>
                  <Table size="small">
                    <TableBody>
                      {dimensionOrder.map(dim => (
                          <TableRow key={dim}>
                            <TableCell sx={{ border: 0, py: 0.5 }}>
                              {STRUCTURAL_DIMENSION_LABEL_KEYS[dim] ? t(STRUCTURAL_DIMENSION_LABEL_KEYS[dim]) : dim}
                              <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                ({STRUCTURAL_DIMENSION_WEIGHTS[dim]})
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ border: 0, py: 0.5 }} align="right">
                              <ScoreBadge score={scores[dim] ?? 0} />
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </Grid>

                {/* Issues list */}
                {issues.length > 0 && (
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                      {t('auditDetail.structural.issuesDetected', { count: issues.length })}
                    </Typography>
                    <List dense disablePadding>
                      {issues.map((issue: any, i: number) => (
                        <ListItem key={i} disableGutters sx={{ alignItems: 'flex-start' }}>
                          <ListItemIcon sx={{ minWidth: 28, mt: 0.5 }}>
                            <Chip
                              label={issue.severity}
                              size="small"
                              color={SEVERITY_COLORS[issue.severity] ?? 'default'}
                              sx={{ fontSize: '0.65rem', height: 20 }}
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={issue.message}
                            secondary={issue.recommendation}
                            primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                            secondaryTypographyProps={{ variant: 'body2' }}
                          />
                          <AddTaskButton
                            title={issue.message}
                            description={issue.recommendation || issue.message}
                            pageUrl={row.pageUrl}
                            dimension={issue.dimension}
                            auditId={auditId}
                            brandId={brandId}
                            originLabel={t('auditDetail.origin.structuralSeo')}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Grid>
                )}

                {/* Dimension-based advice for low scores */}
                {lowDimensions.length > 0 && (
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                      {t('auditDetail.improvementTips')}
                    </Typography>
                    <List dense disablePadding>
                      {lowDimensions.map(dim => {
                        const adviceKey = getStructuralAdviceKey(dim, scores[dim]);
                        if (!adviceKey) return null;
                        const advice = t(adviceKey);
                        const dimLabel = STRUCTURAL_DIMENSION_LABEL_KEYS[dim] ? t(STRUCTURAL_DIMENSION_LABEL_KEYS[dim]) : dim;
                        return (
                          <ListItem key={dim} disableGutters>
                            <ListItemIcon sx={{ minWidth: 32 }}>
                              <LightbulbIcon fontSize="small" sx={{ color: '#FF6B35' }} />
                            </ListItemIcon>
                            <ListItemText
                              primary={`${dimLabel} (${scores[dim]}/100)`}
                              secondary={advice}
                              primaryTypographyProps={{ variant: 'body2', fontWeight: 700 }}
                              secondaryTypographyProps={{ variant: 'body2' }}
                            />
                            <AddTaskButton
                              title={`${dimLabel} — ${row.pageUrl}`}
                              description={advice}
                              pageUrl={row.pageUrl}
                              dimension={dim}
                              auditId={auditId}
                              brandId={brandId}
                              originLabel={t('auditDetail.origin.structuralSeo')}
                            />
                          </ListItem>
                        );
                      })}
                    </List>
                  </Grid>
                )}
              </Grid>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

// --- Freshness advice based on score ---

function getFreshnessAdvice(row: any, t: (key: string, opts?: Record<string, unknown>) => string): string[] {
  const advice: string[] = [];
  const score = row.freshnessScore ?? 0;
  const ageDays = row.ageDays;
  const hasModified = !!row.modifiedDate;
  const hasMachineReadable = row.hasMachineReadableDates;
  const staleness = row.stalenessIndicators ?? [];

  if (score === 0 && ageDays == null) {
    advice.push(t('auditDetail.freshnessAdvice.noDate'));
  }

  if (ageDays != null && ageDays > 365) {
    advice.push(t('auditDetail.freshnessAdvice.overYear', { days: ageDays }));
  } else if (ageDays != null && ageDays > 90) {
    advice.push(t('auditDetail.freshnessAdvice.declining', { days: ageDays }));
  } else if (ageDays != null && ageDays > 45) {
    advice.push(t('auditDetail.freshnessAdvice.declineStart', { days: ageDays }));
  }

  if (!hasModified && ageDays != null && ageDays > 30) {
    advice.push(t('auditDetail.freshnessAdvice.noModified'));
  }

  if (hasMachineReadable === false) {
    advice.push(t('auditDetail.freshnessAdvice.notMachineReadable'));
  }

  if (staleness.length > 0) {
    advice.push(t('auditDetail.freshnessAdvice.stalenessDetected', { count: staleness.length }));
  }

  if (score >= 70 && advice.length === 0) {
    advice.push(t('auditDetail.freshnessAdvice.good'));
  }

  return advice;
}

function FreshnessRow({ row, auditId, brandId }: { row: any; auditId?: string; brandId?: string }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const staleness: string[] = row.stalenessIndicators ?? [];
  const advice = getFreshnessAdvice(row, t);

  return (
    <>
      <TableRow sx={{ '& > *': { borderBottom: 'unset' }, cursor: 'pointer' }} onClick={() => setOpen(!open)}>
        <TableCell>
          <IconButton size="small">
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ maxWidth: 300 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
            <Typography variant="body2" noWrap title={row.pageTitle || row.pageUrl} sx={{ flex: 1, minWidth: 0 }}>
              {row.pageTitle || row.pageUrl}
            </Typography>
            <Tooltip title={t('auditDetail.openPage')}>
              <IconButton
                size="small"
                component="a"
                href={row.pageUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
              >
                <OpenInNewIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          </Box>
        </TableCell>
        <TableCell>{row.publishedDate || '—'}</TableCell>
        <TableCell>{row.modifiedDate || '—'}</TableCell>
        <TableCell>{row.ageDays ?? '—'}</TableCell>
        <TableCell>
          <ScoreBadge score={row.freshnessScore ?? 0} />
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell sx={{ py: 0 }} colSpan={6}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ py: 2, px: 1 }}>
              <Grid container spacing={3}>
                <Grid size={12}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', wordBreak: 'break-all' }}>
                    {row.pageUrl}
                  </Typography>
                </Grid>
                {/* Staleness indicators */}
                {staleness.length > 0 && (
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                      {t('auditDetail.freshness.stalenessIndicators', { count: staleness.length })}
                    </Typography>
                    <List dense disablePadding>
                      {staleness.map((indicator: string, i: number) => (
                        <ListItem key={i} disableGutters>
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <Chip label="!" size="small" color="warning" sx={{ fontSize: '0.65rem', height: 20, width: 20, '& .MuiChip-label': { px: 0 } }} />
                          </ListItemIcon>
                          <ListItemText primary={indicator} primaryTypographyProps={{ variant: 'body2' }} />
                        </ListItem>
                      ))}
                    </List>
                  </Grid>
                )}

                {/* Freshness metadata */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                    {t('auditDetail.freshness.signals')}
                  </Typography>
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell sx={{ border: 0, py: 0.5 }}>{t('auditDetail.table.publishedDate')}</TableCell>
                        <TableCell sx={{ border: 0, py: 0.5 }} align="right">{row.publishedDate || t('auditDetail.notDetected')}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ border: 0, py: 0.5 }}>{t('auditDetail.table.lastModified')}</TableCell>
                        <TableCell sx={{ border: 0, py: 0.5 }} align="right">{row.modifiedDate || t('auditDetail.notDetected')}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ border: 0, py: 0.5 }}>{t('auditDetail.table.ageDays')}</TableCell>
                        <TableCell sx={{ border: 0, py: 0.5 }} align="right">{row.ageDays ?? t('auditDetail.unknown')}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ border: 0, py: 0.5 }}>{t('auditDetail.freshness.machineReadableDates')}</TableCell>
                        <TableCell sx={{ border: 0, py: 0.5 }} align="right">
                          <Chip
                            label={row.hasMachineReadableDates ? t('auditDetail.yes') : t('auditDetail.no')}
                            size="small"
                            color={row.hasMachineReadableDates ? 'success' : 'error'}
                          />
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Grid>

                {/* Advice */}
                {advice.length > 0 && (
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                      {t('auditDetail.improvementTips')}
                    </Typography>
                    <List dense disablePadding>
                      {advice.map((a, i) => (
                        <ListItem key={i} disableGutters>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <LightbulbIcon fontSize="small" sx={{ color: '#FF6B35' }} />
                          </ListItemIcon>
                          <ListItemText primary={a} primaryTypographyProps={{ variant: 'body2' }} />
                          <AddTaskButton
                            title={`${t('auditDetail.origin.freshness')} — ${row.pageUrl}`}
                            description={a}
                            pageUrl={row.pageUrl}
                            dimension="content_freshness"
                            auditId={auditId}
                            brandId={brandId}
                            originLabel={t('auditDetail.origin.freshness')}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Grid>
                )}

                {/* Recommendations from agent */}
                {row.recommendations?.length > 0 && (
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                      {t('auditDetail.recommendations')}
                    </Typography>
                    <List dense disablePadding>
                      {row.recommendations.map((rec: string, i: number) => (
                        <ListItem key={i} disableGutters>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <LightbulbIcon fontSize="small" sx={{ color: '#7C3AED' }} />
                          </ListItemIcon>
                          <ListItemText primary={rec} primaryTypographyProps={{ variant: 'body2' }} />
                          <AddTaskButton
                            title={`${t('auditDetail.origin.freshness')} — ${row.pageUrl}`}
                            description={rec}
                            pageUrl={row.pageUrl}
                            dimension="content_freshness"
                            auditId={auditId}
                            brandId={brandId}
                            originLabel={t('auditDetail.origin.freshness')}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Grid>
                )}
              </Grid>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

// Phase D — "Lisibilite du metier par l'IA". One Haiku synthesis written
// alongside the content_quality summary. Shows the natural-language read of
// the business, a confidence gauge, and the declared-but-missing gap list.
function BusinessReadabilityCard({
  data,
  auditId,
  brandId,
}: {
  data: BusinessReadability;
  auditId?: string;
  brandId?: string;
}) {
  const { t } = useTranslation();
  const confidence = Math.max(0, Math.min(100, Math.round(data.confidence || 0)));
  const readabilityRecos = fromStringList(data.recommendations, {
    auditType: 'website',
    originLabel: t('auditDetail.origin.aiReadability'),
  });
  const confidenceColor =
    confidence >= 70 ? '#10B981' : confidence >= 40 ? '#F59E0B' : '#EF4444';

  return (
    <Box sx={{ px: 3, pt: 2, pb: 1 }}>
      <Box
        sx={{
          p: 2.5,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 1.5 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" fontWeight={700}>
              {t('auditDetail.readability.title')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('auditDetail.readability.subtitle')}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              minWidth: 68,
            }}
          >
            <Typography variant="h6" fontWeight={700} sx={{ color: confidenceColor, lineHeight: 1 }}>
              {confidence}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('auditDetail.readability.confidence')}
            </Typography>
          </Box>
        </Box>

        <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.6 }}>
          {data.synthesis}
        </Typography>

        {data.missing_topics && data.missing_topics.length > 0 && (
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              {t('auditDetail.readability.missingTopics')}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {data.missing_topics.map((t) => (
                <Chip
                  key={`missing-${t}`}
                  label={t}
                  size="small"
                  sx={{
                    bgcolor: 'rgba(255, 107, 53, 0.12)',
                    color: '#FF6B35',
                    fontWeight: 600,
                  }}
                />
              ))}
            </Box>
          </Box>
        )}

        {data.unexpected_topics && data.unexpected_topics.length > 0 && (
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              {t('auditDetail.readability.unexpectedTopics')}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {data.unexpected_topics.map((t) => (
                <Chip
                  key={`unexpected-${t}`}
                  label={t}
                  size="small"
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>
        )}

        {readabilityRecos.length > 0 && (
          <Box sx={{ mb: 1 }}>
            <RecommendationList
              recommendations={readabilityRecos}
              auditId={auditId}
              brandId={brandId}
              title={t('auditDetail.recommendations')}
              dense
            />
          </Box>
        )}

        {data.source_urls && data.source_urls.length > 0 && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mt: 1.5 }}
          >
            {t('auditDetail.readability.sources', { count: data.source_urls.length })}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

// Phase B rollup — shows avg GEO readiness per page type with counts, making the
// per-type weight matrix immediately legible without scanning the table.
// Rendered only when the Phase B summary is present (post-Phase-B audits).
function PageTypeRollup({
  averages,
}: {
  averages: Record<string, { count: number; avg_geo_readiness: number }>;
}) {
  const { t } = useTranslation();
  const entries = Object.entries(averages).sort(
    (a, b) => b[1].avg_geo_readiness - a[1].avg_geo_readiness,
  );
  if (entries.length === 0) return null;

  const colorFor = (score: number): string => {
    if (score >= 70) return '#10B981';
    if (score >= 50) return '#F59E0B';
    return '#EF4444';
  };

  return (
    <Box sx={{ px: 3, pt: 2, pb: 1 }}>
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
        {t('auditDetail.pageTypeRollup.title')}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        {t('auditDetail.pageTypeRollup.subtitle')}
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {entries.map(([pageType, stats]) => (
          <Box
            key={pageType}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 1.5,
              py: 0.75,
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
            }}
          >
            <Typography variant="body2" fontWeight={600}>
              {PAGE_TYPE_LABEL_KEYS[pageType as PageType] ? t(PAGE_TYPE_LABEL_KEYS[pageType as PageType]) : pageType}
            </Typography>
            <Typography
              variant="body2"
              fontWeight={700}
              sx={{ color: colorFor(stats.avg_geo_readiness) }}
            >
              {stats.avg_geo_readiness}/100
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('auditDetail.pageTypeRollup.pageCount', { count: stats.count })}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function ContentQualityRow({ row, auditId, brandId, showBrandAlignment }: { row: any; auditId: string; brandId?: string; showBrandAlignment?: boolean }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState<any>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const handleToggle = async () => {
    if (!open && !detail) {
      setLoadingDetail(true);
      try {
        const urlHash = row.urlHash || row.SK?.replace('CONTENT_QUALITY#', '');
        const data = await auditsApi.getPageDetail(auditId, 'content_quality', urlHash);
        setDetail(data);
      } catch {
        setDetail(null);
      } finally {
        setLoadingDetail(false);
      }
    }
    setOpen(!open);
  };

  return (
    <>
      <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell>
          <IconButton size="small" onClick={handleToggle}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ maxWidth: 280 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
            <Typography variant="body2" noWrap title={row.pageTitle || row.pageUrl} sx={{ flex: 1, minWidth: 0 }}>
              {row.pageTitle || row.pageUrl}
            </Typography>
            <Tooltip title={t('auditDetail.openPage')}>
              <IconButton
                size="small"
                component="a"
                href={row.pageUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
              >
                <OpenInNewIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          </Box>
        </TableCell>
        <TableCell>
          {row.pageType ? (
            <Chip
              label={PAGE_TYPE_LABEL_KEYS[row.pageType as PageType] ? t(PAGE_TYPE_LABEL_KEYS[row.pageType as PageType]) : row.pageType}
              size="small"
              variant="outlined"
            />
          ) : (
            <Typography variant="body2" color="text.secondary">—</Typography>
          )}
        </TableCell>
        <TableCell>{row.scores?.statistics ?? '—'}</TableCell>
        <TableCell>{row.scores?.expert_citations ?? '—'}</TableCell>
        <TableCell>{row.scores?.source_references ?? '—'}</TableCell>
        <TableCell>{row.scores?.fluency ?? '—'}</TableCell>
        <TableCell>
          <ScoreBadge score={row.geoReadiness ?? 0} />
        </TableCell>
        {showBrandAlignment && (
          <TableCell>
            {row.brandAlignment ? (
              <ScoreBadge score={row.brandAlignment.score ?? 0} />
            ) : (
              <Typography variant="body2" color="text.secondary">—</Typography>
            )}
          </TableCell>
        )}
      </TableRow>
      <TableRow>
        <TableCell sx={{ py: 0 }} colSpan={showBrandAlignment ? 9 : 8}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ py: 2, px: 1 }}>
              {loadingDetail ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={20} />
                </Box>
              ) : detail ? (
                <Grid container spacing={3}>
                  <Grid size={12}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', wordBreak: 'break-all' }}>
                      {row.pageUrl}
                    </Typography>
                  </Grid>
                  {/* Signal scores breakdown */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                      {t('auditDetail.contentQuality.geoSignals')}
                    </Typography>
                    <Table size="small">
                      <TableBody>
                        {[
                          [t('auditDetail.contentQuality.signal.statistics'), detail.scores?.statistics],
                          [t('auditDetail.contentQuality.signal.expertCitations'), detail.scores?.expert_citations],
                          [t('auditDetail.contentQuality.signal.sourceReferences'), detail.scores?.source_references],
                          [t('auditDetail.contentQuality.signal.fluency'), detail.scores?.fluency],
                          [t('auditDetail.contentQuality.signal.technicalTerminology'), detail.scores?.technical_terminology],
                          [t('auditDetail.contentQuality.signal.authoritativeTone'), detail.scores?.authoritative_tone],
                          [t('auditDetail.contentQuality.signal.keywordStuffing'), detail.scores?.keyword_stuffing],
                        ].map(([label, score]) => (
                          <TableRow key={label as string}>
                            <TableCell sx={{ border: 0, py: 0.5 }}>{label}</TableCell>
                            <TableCell sx={{ border: 0, py: 0.5 }} align="right">
                              <ScoreBadge score={(score as number) ?? 0} />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Grid>

                  {/* Brand alignment detail */}
                  {detail.brandAlignment && (
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                        {t('auditDetail.summary.brandAlignment')}
                      </Typography>
                      <Table size="small">
                        <TableBody>
                          {detail.brandAlignment.keyword_coverage != null && (
                            <TableRow>
                              <TableCell sx={{ border: 0, py: 0.5 }}>{t('auditDetail.contentQuality.keywordCoverage')}</TableCell>
                              <TableCell sx={{ border: 0, py: 0.5 }} align="right">
                                <ScoreBadge score={detail.brandAlignment.keyword_coverage} />
                              </TableCell>
                            </TableRow>
                          )}
                          {detail.brandAlignment.differentiator_coverage != null && (
                            <TableRow>
                              <TableCell sx={{ border: 0, py: 0.5 }}>{t('auditDetail.contentQuality.differentiatorCoverage')}</TableCell>
                              <TableCell sx={{ border: 0, py: 0.5 }} align="right">
                                <ScoreBadge score={detail.brandAlignment.differentiator_coverage} />
                              </TableCell>
                            </TableRow>
                          )}
                          {detail.brandAlignment.persona_relevance != null && (
                            <TableRow>
                              <TableCell sx={{ border: 0, py: 0.5 }}>{t('auditDetail.contentQuality.personaRelevance')}</TableCell>
                              <TableCell sx={{ border: 0, py: 0.5 }} align="right">
                                <ScoreBadge score={detail.brandAlignment.persona_relevance} />
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                      {detail.brandAlignment.missing_keywords?.length > 0 && (
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            {t('auditDetail.contentQuality.missingKeywords')}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                            {detail.brandAlignment.missing_keywords.map((kw: string) => (
                              <Chip key={kw} label={kw} size="small" variant="outlined" color="warning" />
                            ))}
                          </Box>
                        </Box>
                      )}
                      {detail.brandAlignment.missing_differentiators?.length > 0 && (
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            {t('auditDetail.contentQuality.missingDifferentiators')}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                            {detail.brandAlignment.missing_differentiators.map((d: string) => (
                              <Chip key={d} label={d} size="small" variant="outlined" color="error" />
                            ))}
                          </Box>
                        </Box>
                      )}
                    </Grid>
                  )}

                  {/* Assessment */}
                  {detail.assessment && (
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                        {t('auditDetail.contentQuality.assessment')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                        {detail.assessment}
                      </Typography>
                    </Grid>
                  )}

                  {/* Recommendations */}
                  {detail.recommendations?.length > 0 && (
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                        {t('auditDetail.recommendations')}
                      </Typography>
                      <List dense disablePadding>
                        {detail.recommendations.map((rec: string, i: number) => (
                          <ListItem key={i} disableGutters>
                            <ListItemIcon sx={{ minWidth: 32 }}>
                              <LightbulbIcon fontSize="small" sx={{ color: '#FF6B35' }} />
                            </ListItemIcon>
                            <ListItemText primary={rec} primaryTypographyProps={{ variant: 'body2' }} />
                            <AddTaskButton
                              title={`${t('auditDetail.origin.contentQuality')} — ${row.pageUrl}`}
                              description={rec}
                              pageUrl={row.pageUrl}
                              dimension="content_quality"
                              auditId={auditId}
                              brandId={brandId}
                              originLabel={t('auditDetail.origin.contentQuality')}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Grid>
                  )}
                </Grid>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t('auditDetail.detailUnavailable')}
                </Typography>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

function SummaryCard({ title, value, color }: {
  title: string;
  value: string;
  color: string;
}) {
  return (
    <Card>
      <CardContent sx={{ p: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{title}</Typography>
        <Typography variant="h4" fontWeight={700} sx={{ color }}>{value}</Typography>
      </CardContent>
    </Card>
  );
}

/**
 * "Pages analysées" card — when the audit's page count is capped by the
 * tenant's tier (basic/pro), shows a limit badge + an "Analyser plus de
 * pages" button that opens the upgrade dialog.
 *
 * Self-serve only. The tier gate can NOT be inferred from `limit !== -1`:
 * agency/enterprise are -1 in the registry but `resolveEntitlement` clamps -1
 * up to the systemCeiling (500), so the badge + upsell rendered for them too
 * once an audit crossed that count. Their page budget is contractual.
 */
function PagesAnalyzedCard({ pagesAnalyzed, onUpgrade }: { pagesAnalyzed: number; onUpgrade: () => void }) {
  const { t } = useTranslation();
  const { tenant } = useAuth();
  const limit = resolveEntitlement('pages_per_website_audit', tenant?.tier || 'basic').limit;
  const capped = isSelfServeTier(tenant?.tier) && limit !== -1 && pagesAnalyzed >= limit;

  return (
    <Card>
      <CardContent sx={{ p: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {t('auditDetail.summary.pagesAnalyzed')}
        </Typography>
        <Typography variant="h4" fontWeight={700} sx={{ color: '#8B9DC3' }}>
          {pagesAnalyzed}
        </Typography>
        {capped && (
          <>
            <Chip
              label={t('auditDetail.pagesAnalyzed.limitBadge', { limit })}
              size="small"
              sx={{ mt: 1, mb: 1, bgcolor: '#FEF3C7', color: '#B45309', fontWeight: 700 }}
            />
            <Button size="small" variant="outlined" fullWidth onClick={onUpgrade} sx={{ mt: 0.5, textTransform: 'none' }}>
              {t('auditDetail.pagesAnalyzed.analyzeMore')}
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// --- Phase A: Triage disclosure panel ---

/** i18n keys — resolved with `t()` at render. */
const PAGE_TYPE_LABEL_KEYS: Record<PageType, string> = {
  homepage: 'auditDetail.pageType.homepage',
  product: 'auditDetail.pageType.product',
  about: 'auditDetail.pageType.about',
  pricing: 'auditDetail.pageType.pricing',
  blog: 'auditDetail.pageType.blog',
  case_study: 'auditDetail.pageType.caseStudy',
  docs: 'auditDetail.pageType.docs',
  legal: 'auditDetail.pageType.legal',
  careers: 'auditDetail.pageType.careers',
  contact: 'auditDetail.pageType.contact',
  utility: 'auditDetail.pageType.utility',
  other: 'auditDetail.pageType.other',
};

/** i18n keys — resolved with `t()` at render. */
const TRIAGE_REASON_LABEL_KEYS: Record<TriageReason, string> = {
  kept: 'auditDetail.triageReason.kept.label',
  user_included: 'auditDetail.triageReason.userIncluded.label',
  user_excluded: 'auditDetail.triageReason.userExcluded.label',
  blocklisted: 'auditDetail.triageReason.blocklisted.label',
  duplicate_locale: 'auditDetail.triageReason.duplicateLocale.label',
  category_cap_exceeded: 'auditDetail.triageReason.categoryCapExceeded.label',
  cluster_cap_exceeded: 'auditDetail.triageReason.clusterCapExceeded.label',
  budget_exceeded: 'auditDetail.triageReason.budgetExceeded.label',
};

/** i18n keys — resolved with `t()` at render. */
const TRIAGE_REASON_DESCRIPTION_KEYS: Record<TriageReason, string> = {
  kept: 'auditDetail.triageReason.kept.description',
  user_included: 'auditDetail.triageReason.userIncluded.description',
  user_excluded: 'auditDetail.triageReason.userExcluded.description',
  blocklisted: 'auditDetail.triageReason.blocklisted.description',
  duplicate_locale: 'auditDetail.triageReason.duplicateLocale.description',
  category_cap_exceeded: 'auditDetail.triageReason.categoryCapExceeded.description',
  cluster_cap_exceeded: 'auditDetail.triageReason.clusterCapExceeded.description',
  budget_exceeded: 'auditDetail.triageReason.budgetExceeded.description',
};

function TriagePanel({ triage, brandId }: { triage: TriageLog; brandId?: string }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [reincludedUrls, setReincludedUrls] = useState<Set<string>>(new Set());
  const [pendingUrl, setPendingUrl] = useState<string | null>(null);
  const dropped = triage.decisions.filter((d) => !d.kept);
  const droppedByReason = dropped.reduce<Record<TriageReason, TriageDecision[]>>(
    (acc, d) => {
      if (!acc[d.reason]) acc[d.reason] = [];
      acc[d.reason].push(d);
      return acc;
    },
    {} as Record<TriageReason, TriageDecision[]>,
  );
  const reasonOrder: TriageReason[] = [
    'user_excluded',
    'blocklisted',
    'duplicate_locale',
    'category_cap_exceeded',
    'cluster_cap_exceeded',
    'budget_exceeded',
  ];

  // Full funnel: discovered → selected (triage kept) → fetched (HTTP success) → analyzed.
  // fetchedCount is absent on pre-fix audits; fall back to keptCount so old audits
  // still render without claiming fake fetch numbers.
  const fetchedCount = triage.fetchedCount ?? triage.keptCount;
  const fetchFailures = triage.fetchFailures ?? [];

  if (dropped.length === 0 && fetchFailures.length === 0) {
    return null;
  }

  // Adds `url` to the brand's auditConfig.includeUrls. The audit already ran so this
  // only affects FUTURE audits — we just flip a local flag to signal intent. Preserves
  // any existing includeUrls / excludeUrls by fetching the brand first.
  const handleReinclude = async (url: string) => {
    if (!brandId || reincludedUrls.has(url) || pendingUrl) return;
    setPendingUrl(url);
    try {
      const brand = await api.get<{ auditConfig?: { includeUrls?: string[]; excludeUrls?: string[] } }>(
        `/brands/${brandId}`,
      );
      const existingIncludes = brand.auditConfig?.includeUrls || [];
      const existingExcludes = brand.auditConfig?.excludeUrls || [];
      if (existingIncludes.includes(url)) {
        setReincludedUrls((prev) => new Set(prev).add(url));
        return;
      }
      await api.patch(`/brands/${brandId}`, {
        auditConfig: {
          includeUrls: [...existingIncludes, url],
          excludeUrls: existingExcludes,
        },
      });
      setReincludedUrls((prev) => new Set(prev).add(url));
    } catch {
      // swallow — button stays actionable
    } finally {
      setPendingUrl(null);
    }
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent sx={{ pb: expanded ? 2 : '16px !important' }}>
        <Box
          onClick={() => setExpanded(!expanded)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
            userSelect: 'none',
          }}
        >
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              {t('auditDetail.triage.header', { kept: triage.keptCount, dropped: dropped.length })}
              {fetchFailures.length > 0 && t('auditDetail.triage.headerInaccessible', { count: fetchFailures.length })}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {t('auditDetail.triage.funnel', {
                discovered: triage.discoveredCount,
                kept: triage.keptCount,
                fetched: fetchedCount,
              })}
            </Typography>
          </Box>
          <IconButton size="small">
            {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </Box>

        <Collapse in={expanded}>
          <Box sx={{ mt: 2 }}>
            {reasonOrder.map((reason) => {
              const items = droppedByReason[reason];
              if (!items || items.length === 0) return null;
              return (
                <Box key={reason} sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Typography variant="body2" fontWeight={600}>
                      {t(TRIAGE_REASON_LABEL_KEYS[reason])}
                    </Typography>
                    <Chip label={items.length} size="small" />
                  </Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mb: 1 }}
                  >
                    {t(TRIAGE_REASON_DESCRIPTION_KEYS[reason])}
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>{t('auditDetail.triage.url')}</TableCell>
                          <TableCell>{t('auditDetail.triage.category')}</TableCell>
                          {reason === 'duplicate_locale' && <TableCell>{t('auditDetail.triage.retainedVersion')}</TableCell>}
                          {brandId && reason !== 'user_excluded' && (
                            <TableCell align="right">{t('auditDetail.triage.action')}</TableCell>
                          )}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {items.map((d) => {
                          const alreadyReincluded = reincludedUrls.has(d.url);
                          const isPending = pendingUrl === d.url;
                          return (
                            <TableRow key={d.url} hover>
                              <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8125rem' }}>
                                <a
                                  href={d.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ color: 'inherit', textDecoration: 'none' }}
                                >
                                  {d.url}
                                </a>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={PAGE_TYPE_LABEL_KEYS[d.pageType] ? t(PAGE_TYPE_LABEL_KEYS[d.pageType]) : d.pageType}
                                  size="small"
                                  variant="outlined"
                                />
                              </TableCell>
                              {reason === 'duplicate_locale' && (
                                <TableCell
                                  sx={{ fontFamily: 'monospace', fontSize: '0.8125rem' }}
                                >
                                  {d.canonicalUrl || '—'}
                                </TableCell>
                              )}
                              {brandId && reason !== 'user_excluded' && (
                                <TableCell align="right">
                                  <Tooltip
                                    title={
                                      alreadyReincluded
                                        ? t('auditDetail.triage.reincludedTip')
                                        : t('auditDetail.triage.reincludeTip')
                                    }
                                  >
                                    <span>
                                      <IconButton
                                        size="small"
                                        onClick={() => handleReinclude(d.url)}
                                        disabled={alreadyReincluded || isPending}
                                      >
                                        {isPending ? (
                                          <CircularProgress size={14} />
                                        ) : alreadyReincluded ? (
                                          <CheckCircleIcon
                                            fontSize="small"
                                            sx={{ color: '#00DDB3' }}
                                          />
                                        ) : (
                                          <AddCircleOutlineIcon
                                            fontSize="small"
                                            sx={{ color: '#7C3AED' }}
                                          />
                                        )}
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                </TableCell>
                              )}
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              );
            })}

            {fetchFailures.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Typography variant="body2" fontWeight={600}>
                    {t('auditDetail.triage.inaccessibleTitle')}
                  </Typography>
                  <Chip label={fetchFailures.length} size="small" color="warning" />
                </Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mb: 1 }}
                >
                  {t('auditDetail.triage.inaccessibleDescription')}
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('auditDetail.triage.url')}</TableCell>
                        <TableCell>{t('auditDetail.triage.reason')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {fetchFailures.map((f) => (
                        <TableRow key={f.url} hover>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8125rem' }}>
                            <a
                              href={f.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: 'inherit', textDecoration: 'none' }}
                            >
                              {f.url}
                            </a>
                          </TableCell>
                          <TableCell>
                            <Chip label={f.reason} size="small" variant="outlined" color="warning" />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
}
